import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Section04.module.css';
import apiService from '../../services/api';

function Section04() {
    const navigate = useNavigate();
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(3);
    const carouselWrapperRef = useRef(null);
    const cardRef = useRef(null);

    // 뉴스 데이터 가져오기
    useEffect(() => {
        const fetchNewsData = async () => {
            setLoading(true);
            try {
                const [noticesRes, pressRes, seminarsRes] = await Promise.all([
                    apiService.getNotices(1, 10).catch(() => ({ notices: [] })),
                    apiService.getPressList(1, 10).catch(() => ({ presses: [] })),
                    apiService.getSeminars(1, 10).catch(() => ({ seminars: [] }))
                ]);

                const notices = (noticesRes.notices || noticesRes || []).map(item => ({
                    id: item.id,
                    category: '공지사항',
                    title: item.title || '',
                    description: item.content || item.description || '',
                    image: item.imageUrl || '/image/20160107000821_0.jpg',
                    date: item.date,
                    type: 'notice'
                }));

                const presses = (pressRes.presses || pressRes || []).map(item => ({
                    id: item.id,
                    category: '언론보도',
                    title: item.title || '',
                    description: item.content || item.description || '',
                    image: item.imageUrl || '/image/20160107000821_0.jpg',
                    date: item.date,
                    type: 'press'
                }));

                const seminars = (seminarsRes.seminars || seminarsRes || []).map(item => ({
                    id: item.id,
                    category: '세미나 & 컨퍼런스',
                    title: item.title || '',
                    description: item.content || item.description || '',
                    image: item.imageUrl || '/image/20160107000821_0.jpg',
                    date: item.date,
                    type: 'seminar'
                }));

                const allNews = [...notices, ...presses, ...seminars].sort((a, b) => {
                    const dateA = a.date ? new Date(a.date).getTime() : 0;
                    const dateB = b.date ? new Date(b.date).getTime() : 0;
                    return dateB - dateA;
                });

                setNewsData(allNews);
            } catch (error) {
                console.error('뉴스 데이터 가져오기 실패:', error);
                setNewsData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchNewsData();
    }, []);

    // 화면 크기에 따라 보이는 카드 개수 계산
    useEffect(() => {
        const updateItemsPerView = () => {
            const width = window.innerWidth;
            if (width <= 768) {
                setItemsPerView(1);
            } else if (width <= 1024) {
                setItemsPerView(2);
            } else {
                setItemsPerView(3);
            }
        };

        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, []);

    // 카드 이동 거리 계산
    const getTranslateX = () => {
        if (!carouselWrapperRef.current || !cardRef.current || newsData.length === 0) {
            return `translateX(calc(-${currentIndex} * (100% / ${itemsPerView})))`;
        }

        const wrapperWidth = carouselWrapperRef.current.offsetWidth;
        const cardWidth = cardRef.current.offsetWidth;
        
        // 모바일에서는 wrapper width를 기준으로 계산
        if (itemsPerView === 1) {
            return `translateX(-${currentIndex * wrapperWidth}px)`;
        }
        
        const cardMargin = 40;
        const cardTotalWidth = cardWidth + cardMargin;

        return `translateX(-${currentIndex * cardTotalWidth}px)`;
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        // 현재 화면 크기에 맞는 개수만큼 보이므로 마지막 인덱스는 전체 길이 - itemsPerView
        setCurrentIndex((prev) => Math.min(newsData.length - itemsPerView, prev + 1));
    };

    const handleNewsClick = (news) => {
        navigate(`/news/${news.type}/${news.id}`);
    };

    if (loading) {
        return (
            <div className={styles.section04}>
                <div className={styles.container}>
                    <h2 className={styles.title}>News</h2>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        로딩 중...
                    </div>
                </div>
            </div>
        );
    }

    if (newsData.length === 0) {
        return (
            <div className={styles.section04}>
                <div className={styles.container}>
                    <h2 className={styles.title}>News</h2>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        뉴스가 없습니다.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.section04}>
            <div className={styles.container}>
                <h2 className={styles.title}>News</h2>

                <div className={styles.carouselContainer}>
                    <button
                        className={styles.navButton}
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        aria-label="이전"
                    >
                         <img src="/image/section04-arrow-left.png" alt="arrow" />
                    </button>

                    <div className={styles.carouselWrapper} ref={carouselWrapperRef}>
                        <div
                            className={styles.carouselTrack}
                            style={{ transform: getTranslateX() }}
                        >
                            {newsData.map((news, index) => (
                                <div
                                    key={`${news.type}-${news.id}`}
                                    className={styles.newsCard}
                                    ref={index === 0 ? cardRef : null}
                                    onClick={() => handleNewsClick(news)}
                                >
                                    <div className={styles.imageWrapper}>
                                        <img src={news.image} alt={news.title} className={styles.image} />
                                    </div>
                                    <div className={styles.content}>
                                        <span className={styles.category}>{news.category}</span>
                                        <h3 className={styles.newsTitle}>{news.title}</h3>
                                        <p className={styles.description}>
                                            {news.description
                                                ? news.description.replace(/<[^>]*>/g, '').substring(0, 100) + '...'
                                                : '내용이 없습니다.'
                                            }
                                        </p>
                                        <div className={styles.moreButtonWrapper}>
                                            <button
                                                className={styles.moreButton}
                                                aria-label="더보기"
                                            >
                                                <img src="/image/section04-arrow-pink.png" alt="arrow" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        className={styles.navButton}
                        onClick={handleNext}
                        disabled={currentIndex >= newsData.length - itemsPerView}
                        aria-label="다음"
                    >
                        <img src="/image/section04-arrow-right.png" alt="arrow" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Section04;